import { z } from 'zod'

/** PROMPT.md §5.3. Decides whether and how a privileged command gets a sudo prefix. */
export const sudoModeSchema = z.enum(['none', 'nopasswd', 'password', 'ask'])
export type SudoMode = z.infer<typeof sudoModeSchema>

/** Fixed palette so the UI can map a colour to classes without accepting arbitrary CSS. */
export const serverColorSchema = z.enum([
  'slate',
  'red',
  'amber',
  'emerald',
  'sky',
  'violet',
  'pink',
])
export type ServerColor = z.infer<typeof serverColorSchema>

/** `prod` is special: §6.5 asks for a visibly different treatment and stricter confirmations. */
export const PRODUCTION_GROUP_TAG = 'prod'

export const groupTagSchema = z
  .string()
  .trim()
  .min(1, 'Gruppe darf nicht leer sein.')
  .max(40, 'Gruppe darf höchstens 40 Zeichen lang sein.')

export const authKindSchema = z.enum(['key-file', 'key-text', 'password'])
export type AuthKind = z.infer<typeof authKindSchema>

/**
 * What the client sends when it creates or updates a server. This carries real secrets: the
 * backend moves them straight into the vault and never writes them to SQLite.
 */
export const serverAuthInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('key-file'),
    /** Path on the machine the backend runs on — which may be a VPS, not the user's PC (§3.1). */
    path: z.string().trim().min(1, 'Pfad zur Schlüsseldatei fehlt.'),
    passphrase: z.string().optional(),
  }),
  z.object({
    kind: z.literal('key-text'),
    privateKey: z.string().min(1, 'Der Schlüssel ist leer.'),
    passphrase: z.string().optional(),
  }),
  z.object({
    kind: z.literal('password'),
    password: z.string().min(1, 'Das Passwort ist leer.'),
  }),
])
export type ServerAuthInput = z.infer<typeof serverAuthInputSchema>

/**
 * What the API gives back. Never a secret — only whether one exists, plus the key path, which is
 * a location and not a credential.
 */
export const serverAuthSummarySchema = z.object({
  kind: authKindSchema,
  keyPath: z.string().nullable(),
  hasPassphrase: z.boolean(),
  hasSudoPassword: z.boolean(),
})
export type ServerAuthSummary = z.infer<typeof serverAuthSummarySchema>

/** Trust on first use (§5.1): pinned once, and a later mismatch blocks the connection. */
export const hostKeySchema = z.object({
  algorithm: z.string(),
  /** OpenSSH notation, e.g. `SHA256:Yg8…`. */
  fingerprint: z.string(),
  trustedAt: z.iso.datetime(),
})
export type HostKey = z.infer<typeof hostKeySchema>

export const serverSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string(),
  port: z.number().int(),
  username: z.string(),
  groupTag: z.string(),
  color: serverColorSchema,
  osFamily: z.string().nullable(),
  notes: z.string(),
  sudoMode: sudoModeSchema,
  /** Null when the database has a server the vault has no credential for. */
  auth: serverAuthSummarySchema.nullable(),
  hostKey: hostKeySchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
})
export type Server = z.infer<typeof serverSchema>

const serverFields = {
  name: z
    .string()
    .trim()
    .min(1, 'Name darf nicht leer sein.')
    .max(80, 'Name darf höchstens 80 Zeichen lang sein.'),
  host: z
    .string()
    .trim()
    .min(1, 'Host darf nicht leer sein.')
    .max(255, 'Host darf höchstens 255 Zeichen lang sein.'),
  port: z
    .number('Port muss eine Zahl sein.')
    .int('Port muss eine ganze Zahl sein.')
    .min(1, 'Port muss zwischen 1 und 65535 liegen.')
    .max(65535, 'Port muss zwischen 1 und 65535 liegen.'),
  username: z
    .string()
    .trim()
    .min(1, 'Benutzername darf nicht leer sein.')
    .max(64, 'Benutzername darf höchstens 64 Zeichen lang sein.'),
  groupTag: groupTagSchema,
  color: serverColorSchema,
  notes: z.string().max(2000, 'Notizen dürfen höchstens 2000 Zeichen lang sein.'),
  sudoMode: sudoModeSchema,
}

export const createServerSchema = z.object({
  ...serverFields,
  port: serverFields.port.default(22),
  groupTag: serverFields.groupTag.default('default'),
  color: serverFields.color.default('slate'),
  notes: serverFields.notes.default(''),
  sudoMode: serverFields.sudoMode.default('none'),
  auth: serverAuthInputSchema,
  /** Required when sudoMode is `password`; ignored otherwise. */
  sudoPassword: z.string().optional(),
})
export type CreateServerInput = z.infer<typeof createServerSchema>

/**
 * Every field optional. `auth` and `sudoPassword` are only touched when present, so editing a
 * server's name does not require re-entering its key.
 */
export const updateServerSchema = z
  .object({
    ...serverFields,
    auth: serverAuthInputSchema,
    sudoPassword: z.string().nullable(),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Es wurde kein Feld zum Ändern übergeben.',
  })
export type UpdateServerInput = z.infer<typeof updateServerSchema>

export const serverIdParamsSchema = z.object({ id: z.string().min(1) })

export const trustHostKeySchema = z.object({
  /** Echoed back from the test result, so a stale dialog cannot pin the wrong key. */
  fingerprint: z.string().min(1),
})
